import { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import DirectionsIcon from "@mui/icons-material/Directions";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import RefreshIcon from "@mui/icons-material/Refresh";
import CircularProgress from "@mui/material/CircularProgress";
import { Navigate } from "react-router-dom";
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  OutlinedInput,
  Button,
  Grid,
} from "@mui/material";
import Link from "@mui/material/Link";

import Navbar from "../../components/Navbar";
import MultiSelectCheckbox from "../../components/MultiSelectCheckbox";
import zipzyAdminApiClient from "../../apiClients/zipzyAdminApiClient";
import { DEFAULT_LOCALITY_IDS } from "../../constants";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  Legend,
} from "recharts";

// Internal user IDs for internal completed rides tracking
const INTERNAL_USER_IDS = [
  "691dbcdabb70b8252d89b03d",
  "68a3020d4380da6a0989b040",
  "690eec124f496f486a89b040",
];

function getISTDateString(inputDate) {
  const date = new Date(inputDate);
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

function generateDateRange(start, end) {
  const dates = [];
  const current = new Date(start);

  current.setHours(0, 0, 0, 0);
  const targetEnd = new Date(end);
  targetEnd.setHours(23, 59, 59, 999);

  while (current <= targetEnd) {
    dates.push(getISTDateString(current));
    current.setDate(current.getDate() + 1);
  }
  return dates;
}

function formatXAxisDate(dateStr) {
  if (!dateStr) return "";
  const parts = dateStr.split("-");
  if (parts.length !== 3) return dateStr;
  const [year, month, day] = parts;
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const m = parseInt(month, 10) - 1;
  return `${monthNames[m]} ${parseInt(day, 10)}`;
}

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <Box
        sx={{
          background: "linear-gradient(135deg, rgba(15,23,42,0.97) 0%, rgba(30,41,59,0.97) 100%)",
          backdropFilter: "blur(16px)",
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: "12px",
          padding: "12px 16px",
          boxShadow: "0 20px 60px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.04)",
          textAlign: "left",
          minWidth: 160,
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 700, color: "rgba(255,255,255,0.5)", letterSpacing: "0.08em", textTransform: "uppercase", fontSize: "10px" }}>
          {label}
        </Typography>
        {payload.map((entry, index) => (
          <Box key={index} display="flex" alignItems="center" justifyContent="space-between" gap={2} sx={{ mt: 1 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: entry.color, boxShadow: `0 0 6px ${entry.color}` }} />
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)", fontSize: "11px" }}>
                {entry.name}
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ color: "#fff", fontWeight: 700, fontSize: "12px" }}>
              {entry.name.includes("Revenue") ? `₹${entry.value}` : entry.name.includes("Distance") ? `${entry.value} km` : entry.name.includes("Duration") ? `${entry.value} mins` : entry.value}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  }
  return null;
};

const BASE_FARE_FOR_FIRST_2_KM = 50;
const FARE_PER_KM_AFTER_2_KM = 25;

function SingleSelect({
  label = "Select",
  options = [],
  value = "", // single _id
  onChange,
  width = 300,
}) {
  const handleChange = (event) => {
    onChange?.(event.target.value);
  };

  return (
    <FormControl
      sx={{
        width: "80%",
        mb: 1,
      }}
    >
      <InputLabel>{label}</InputLabel>

      <Select
        value={value}
        onChange={handleChange}
        input={<OutlinedInput label={label} />}
      >
        {options.map((option) => (
          <MenuItem key={option._id} value={option._id}>
            {option.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function getISTNow() {
  // Current time in user's local timezone (IST device)
  return new Date();
}

function toUTC(date) {
  // Convert to UTC ISO string (or return Date object if you prefer)
  return new Date(date.toISOString());
}

function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function startOfWeek(date) {
  const d = startOfDay(date);
  const day = d.getDay(); // 0 (Sun) – 6 (Sat)
  const diff = d.getDate() - day; // assuming week starts Sunday
  d.setDate(diff);
  return d;
}

function startOfMonth(date) {
  const d = startOfDay(date);
  d.setDate(1);
  return d;
}

function getStartTime(selectedTimeRangeOption) {
  const nowIST = getISTNow();
  let startIST;

  switch (selectedTimeRangeOption) {
    case "today":
      startIST = startOfDay(nowIST);
      break;

    case "last_one_day":
      startIST = new Date(nowIST);
      startIST.setDate(startIST.getDate() - 1);
      break;

    case "this_week":
      startIST = startOfWeek(nowIST);
      break;

    case "last_seven_days":
      startIST = new Date(nowIST);
      startIST.setDate(startIST.getDate() - 7);
      break;

    case "this_month":
      startIST = startOfMonth(nowIST);
      break;

    case "last_thirty_days":
      startIST = new Date(nowIST);
      startIST.setDate(startIST.getDate() - 30);
      break;

    // case "all_time":
    //   return null; // or very old date like new Date(0)

    default:
      startIST = new Date(nowIST);
      startIST.setDate(startIST.getDate() - 30);
      break;
  }

  if (!startIST) {
    return null;
  }

  // Convert IST-based moment → UTC
  return toUTC(startIST);
}

function formatToIST(inputDate) {
  const date = new Date(inputDate);

  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  })
    .format(date)
    .replace(",", "");
}

function formatDistanceComputationMethod(methodEnum) {
  if (methodEnum === "HAVERSINE") {
    return "Point-to-Point";
  }

  return methodEnum
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join("");
}

function getDistanceFromFare(fare) {
  if (fare <= BASE_FARE_FOR_FIRST_2_KM) {
    return 2;
  }

  return 2 + (fare - BASE_FARE_FOR_FIRST_2_KM) / FARE_PER_KM_AFTER_2_KM;
}

export default function RidesAnalyticsPage() {
  const isLoggedIn = zipzyAdminApiClient.isLoggedIn();

  const timeRangeOptions = [
    { _id: "today", name: "Today" },
    { _id: "last_one_day", name: "Last 1 Day" },
    { _id: "this_week", name: "This Week" },
    { _id: "last_seven_days", name: "Last 7 Days" },
    { _id: "this_month", name: "This Month" },
    { _id: "last_thirty_days", name: "Last 30 Days" },
    // { _id: "all_time", name: "All Time" },
  ];

  const [isLoadingFilterInfo, setIsLoadingFilterInfo] = useState(true);
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false);

  const [allLocalities, setAllLocalities] = useState([]);
  const [allDrivers, setAllDrivers] = useState([]);
  const [allDriversForSelectedLocalities, setAllDriversForSelectedLocalities] =
    useState([]);

  const [selectedLocalityIds, setSelectedLocalityIds] =
    useState(DEFAULT_LOCALITY_IDS);
  const [selectedDriverIds, setSelectedDriverIds] = useState([]);
  const [selectedTimeRangeOptions, setSelectedTimeRangeOptions] =
    useState("last_one_day");

  const [fetchedRides, setFetchedRides] = useState([]);
  const [ridesStats, setRidesStats] = useState(null);
  const [chartData, setChartData] = useState([]);

  const fetchFilterInfoFromApi = async () => {
    setIsLoadingFilterInfo(true);
    const response = await zipzyAdminApiClient.getFilterInfo();
    if (!response.success) {
      window.alert(response.error.userFriendlyMessage);
      setIsLoadingFilterInfo(false);
      return;
    }

    setAllLocalities(response.data.localities);
    setAllDrivers(response.data.drivers);
    setIsLoadingFilterInfo(false);
  };

  const handleRunQueryButtonClick = async () => {
    setIsLoadingAnalytics(true);
    setFetchedRides([]);
    setRidesStats(null);
    setChartData([]);

    let startTime = null;
    let endTime = null;

    if (selectedTimeRangeOptions) {
      startTime = getStartTime(selectedTimeRangeOptions);
    }

    const response = await zipzyAdminApiClient.getRidesStats({
      localityIds: selectedLocalityIds,
      driverIds: selectedDriverIds,
      startTime,
      endTime,
    });
    if (!response.success) {
      window.alert(response.error.userFriendlyMessage);
      setIsLoadingAnalytics(false);
      return;
    }
    setFetchedRides(response.data.rides);
    setIsLoadingAnalytics(false);

    const ridesStats = {
      createdRides: 0,
      completedRides: 0,
      autoCancelledRides: 0,
      manuallyCancelledRides: 0,
      inProgressRides: 0,

      okCompletedRides: [],
      okAutoCancelledRides: [],
      okManuallyCancelledRides: [],
      okInProgressRides: [],
    };

    for (const ride of response.data.rides) {
      ridesStats.createdRides += 1;

      if (ride.status === "COMPLETED") {
        ridesStats.completedRides += 1;
        ridesStats.okCompletedRides.push(ride);
      } else if (ride.status === "CANCELLED__START_TIMEOUT") {
        ridesStats.autoCancelledRides += 1;
        ridesStats.okAutoCancelledRides.push(ride);
      } else if (ride.status === "CANCELLED__MANUAL") {
        ridesStats.manuallyCancelledRides += 1;
        ridesStats.okManuallyCancelledRides.push(ride);
      } else if (ride.status === "IN_PROGRESS") {
        ridesStats.inProgressRides += 1;
        ridesStats.okInProgressRides.push(ride);
      }
    }

    ridesStats.okCompletedRides.sort(
      (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    );
    ridesStats.okAutoCancelledRides.sort(
      (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    );
    ridesStats.okManuallyCancelledRides.sort(
      (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    );
    ridesStats.okInProgressRides.sort(
      (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    );

    setRidesStats(ridesStats);

    // Group and aggregate rides by day for charts
    let start = startTime;
    let end = new Date();

    if (!start && response.data.rides.length > 0) {
      const times = response.data.rides.map((r) => new Date(r.createdAt).getTime());
      start = new Date(Math.min(...times));
      end = new Date(Math.max(...times));
    }
    if (!start) {
      start = new Date();
      start.setDate(start.getDate() - 30);
    }

    const dates = generateDateRange(start, end);
    const dailyDataMap = {};
    for (const dStr of dates) {
      dailyDataMap[dStr] = {
        dateStr: dStr,
        dateLabel: formatXAxisDate(dStr),
        completed: 0,
        autoCancelled: 0,
        manuallyCancelled: 0,
        internalCompleted: 0,
        revenue: 0,
        distance: 0,
        totalDurationMinutes: 0,
        completedRideCount: 0,
      };
    }

    for (const ride of response.data.rides) {
      const rideDayStr = getISTDateString(ride.createdAt);
      if (!dailyDataMap[rideDayStr]) {
        dailyDataMap[rideDayStr] = {
          dateStr: rideDayStr,
          dateLabel: formatXAxisDate(rideDayStr),
          completed: 0,
          autoCancelled: 0,
          manuallyCancelled: 0,
          internalCompleted: 0,
          revenue: 0,
          distance: 0,
          totalDurationMinutes: 0,
          completedRideCount: 0,
        };
      }

      const fareVal = ride.fare?.value || 0;
      const rideDistance = ride.distance || 0;

      dailyDataMap[rideDayStr].revenue += fareVal;
      dailyDataMap[rideDayStr].distance += rideDistance;

      const rideCustomerId = ride.customer?._id?.toString() ?? ride.customer?.toString();
      if (ride.status === "COMPLETED") {
        dailyDataMap[rideDayStr].completed += 1;
        if (rideCustomerId && INTERNAL_USER_IDS.includes(rideCustomerId)) {
          dailyDataMap[rideDayStr].internalCompleted += 1;
        }
        const durationMs = new Date(ride.updatedAt) - new Date(ride.createdAt);
        if (durationMs > 0) {
          dailyDataMap[rideDayStr].totalDurationMinutes += durationMs / (1000 * 60);
          dailyDataMap[rideDayStr].completedRideCount += 1;
        }
      } else if (ride.status === "CANCELLED__START_TIMEOUT") {
        dailyDataMap[rideDayStr].autoCancelled += 1;
      } else if (ride.status === "CANCELLED__MANUAL") {
        dailyDataMap[rideDayStr].manuallyCancelled += 1;
      }
    }

    const chartDataArray = Object.values(dailyDataMap)
      .map((item) => ({
        ...item,
        revenue: Math.round(item.revenue * 100) / 100,
        distance: Math.round((item.distance / 1000) * 100) / 100,
        avgDuration:
          item.completedRideCount > 0
            ? Math.round((item.totalDurationMinutes / item.completedRideCount) * 10) / 10
            : 0,
      }))
      .sort((a, b) => a.dateStr.localeCompare(b.dateStr));

    setChartData(chartDataArray);
  };

  function getGoogleMapsRouteUrl(ride) {
    const waypointsLocations = [];

    // Start location
    waypointsLocations.push(ride.pickupLocation);

    // Intermediate stops
    for (const stop of ride.stops) {
      waypointsLocations.push({
        latitude: stop.lat,
        longitude: stop.long,
      });
    }

    // Final destination
    waypointsLocations.push(ride.destinationLocation);

    if (waypointsLocations.length < 2) {
      return null;
    }

    const formatLocation = (loc) => `${loc.latitude},${loc.longitude}`;

    const origin = formatLocation(waypointsLocations[0]);
    const destination = formatLocation(
      waypointsLocations[waypointsLocations.length - 1],
    );

    const waypoints = waypointsLocations
      .slice(1, -1)
      .map(formatLocation)
      .join("|");

    let url = `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(
      origin,
    )}&destination=${encodeURIComponent(destination)}&travelmode=driving`;

    if (waypoints) {
      url += `&waypoints=${encodeURIComponent(waypoints)}`;
    }

    return url;
  }

  useEffect(() => {
    document.title = "Rides Stats - Zipzy Admin";

    if (!isLoggedIn) {
      return;
    }

    fetchFilterInfoFromApi();
  }, []);

  useEffect(() => {
    const convertedDrivers = allDrivers.map((driver) => {
      return {
        locality: driver.locality,
        name: driver.fullName,
        _id: driver._id,
      };
    });

    if (selectedLocalityIds.length === 0) {
      setAllDriversForSelectedLocalities(convertedDrivers);
      return;
    }

    const filteredDrivers = convertedDrivers.filter((driver) =>
      selectedLocalityIds.includes(driver.locality),
    );

    setAllDriversForSelectedLocalities(filteredDrivers);
  }, [selectedLocalityIds, allDrivers]);

  useEffect(() => {
    console.log(allDriversForSelectedLocalities);
    setSelectedDriverIds([]);
  }, [allDriversForSelectedLocalities]);

  if (!isLoggedIn) {
    return <Navigate to="/auth/login" replace />;
  }

  if (isLoadingFilterInfo) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="flex-start"
        alignItems="center"
        height="100vh"
        textAlign="center"
      >
        <Navbar />

        <Box
          sx={{
            mb: 4,
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
            width: "90%",
          }}
        >
          <CircularProgress size={24} />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="flex-start"
      alignItems="center"
      height="100vh"
      textAlign="center"
      sx={{
        pb: 10,
      }}
    >
      <Navbar />

      <Box
        sx={{
          mb: 1,
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          alignItems: "center",
          width: "90%",
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: "bold",
          }}
        >
          Rides Statistics
        </Typography>
      </Box>

      <MultiSelectCheckbox
        label="Select Localities"
        options={allLocalities}
        value={selectedLocalityIds}
        onChange={setSelectedLocalityIds}
      />

      <MultiSelectCheckbox
        label="Select Drivers"
        options={allDriversForSelectedLocalities}
        value={selectedDriverIds}
        onChange={setSelectedDriverIds}
      />

      <SingleSelect
        label="Select Time Range"
        options={timeRangeOptions}
        value={selectedTimeRangeOptions}
        onChange={setSelectedTimeRangeOptions}
      />

      <Box
        sx={{
          mb: 1,
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-end",
          alignItems: "center",
          width: "80%",
        }}
      >
        <Button
          color="primary"
          variant="contained"
          disabled={isLoadingAnalytics}
          onClick={handleRunQueryButtonClick}
        >
          Run Query
        </Button>
      </Box>

      {isLoadingAnalytics && (
        <Box
          sx={{
            mt: 2,
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
            width: "90%",
          }}
        >
          <CircularProgress size={24} />
        </Box>
      )}

      {ridesStats !== null && (
        <Box
          width="90%"
          display="flex"
          flexDirection="row"
          justifyContent="flex-start"
          alignItems="center"
          sx={{ mb: 1 }}
        >
          <Typography variant="p">
            Selected{" "}
            <strong>
              {selectedLocalityIds.length !== 0
                ? selectedLocalityIds.length
                : allLocalities.length}{" "}
              localities
            </strong>{" "}
            and{" "}
            <strong>
              {selectedDriverIds.length !== 0
                ? selectedDriverIds.length
                : allDriversForSelectedLocalities.length}{" "}
              drivers
            </strong>
          </Typography>
        </Box>
      )}

      {ridesStats !== null && (
        <Box
          width="90%"
          display="flex"
          flexDirection="column"
          justifyContent="flex-start"
          alignItems="flex-start"
          sx={{ mb: 1 }}
        >
          <Typography variant="p">
            <strong>Created Rides: </strong> {ridesStats.createdRides}
          </Typography>

          <Typography variant="p">
            <strong>Completed Rides: </strong> {ridesStats.completedRides}
          </Typography>

          <Typography variant="p">
            <strong>In Progress Rides: </strong> {ridesStats.inProgressRides}
          </Typography>

          <Typography variant="p">
            <strong>Auto Cancelled Rides: </strong>{" "}
            {ridesStats.autoCancelledRides}
          </Typography>

          <Typography variant="p">
            <strong>Manually Cancelled Rides: </strong>{" "}
            {ridesStats.manuallyCancelledRides}
          </Typography>
        </Box>
      )}

      {/* Charts Section */}
      {ridesStats !== null && chartData.length > 0 && (
        <Box sx={{ my: 5, alignSelf: "stretch", px: "5%" }}>
          {/* Section heading */}
          <Box sx={{ mb: 1, display: "flex", alignItems: "center", gap: 2 }}>
            <Box sx={{ width: 4, height: 28, borderRadius: 2, background: "linear-gradient(180deg, #6366f1, #06b6d4)" }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a", letterSpacing: "-0.02em" }}>
              Analytics Charts
            </Typography>
          </Box>


          <Grid
            container
            spacing={3}
            sx={{ width: "100%", margin: 0, justifyContent: "space-around", flexWrap: "wrap" }}
          >
            {/* Chart 1: Rides Count vs Days */}
            <Grid item sx={{ width: { xs: "100%", sm: "47%" } }}>
              <Card
                sx={{
                  background: "linear-gradient(145deg, #ffffff 0%, #f0f4ff 100%)",
                  border: "1px solid rgba(99,102,241,0.15)",
                  borderTop: "3px solid #6366f1",
                  boxShadow: "0 4px 24px rgba(99,102,241,0.08), 0 1px 4px rgba(0,0,0,0.04)",
                  borderRadius: "16px",
                  transition: "transform 0.25s ease, box-shadow 0.25s ease",
                  "&:hover": {
                    transform: "translateY(-5px)",
                    boxShadow: "0 16px 48px rgba(99,102,241,0.15)",
                  },
                }}
              >
                <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
                  <Box display="flex" alignItems="center" gap={1.5} sx={{ mb: 3 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: "50%", background: "linear-gradient(135deg,#6366f1,#818cf8)", boxShadow: "0 0 8px rgba(99,102,241,0.5)" }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#1e1b4b", letterSpacing: "-0.01em" }}>
                      Rides Trend
                    </Typography>
                    <Box sx={{ ml: "auto", px: 1.5, py: 0.3, borderRadius: "20px", background: "rgba(99,102,241,0.1)", border: "1px solid rgba(99,102,241,0.2)" }}>
                      <Typography variant="caption" sx={{ color: "#6366f1", fontWeight: 600, fontSize: "10px" }}>by Status</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ width: "100%", height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={chartData}
                        margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                        <XAxis
                          dataKey="dateLabel"
                          tick={{ fill: "#6b7280", fontSize: 12 }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <YAxis
                          tick={{ fill: "#6b7280", fontSize: 12 }}
                          axisLine={false}
                          tickLine={false}
                          allowDecimals={false}
                        />
                        <ChartTooltip content={<CustomTooltip />} />

                        <Line
                          name="Completed Rides"
                          type="monotone"
                          dataKey="completed"
                          stroke="#2563eb"
                          strokeWidth={3}
                          dot={{ r: 4, strokeWidth: 0, fill: "#2563eb" }}
                          activeDot={{ r: 8, strokeWidth: 0 }}
                        />
                        <Line
                          name="Auto Cancelled Rides"
                          type="monotone"
                          dataKey="autoCancelled"
                          stroke="#ef4444"
                          strokeWidth={3}
                          dot={{ r: 4, strokeWidth: 0, fill: "#ef4444" }}
                          activeDot={{ r: 8, strokeWidth: 0 }}
                        />
                        <Line
                          name="Manually Cancelled Rides"
                          type="monotone"
                          dataKey="manuallyCancelled"
                          stroke="#f59e0b"
                          strokeWidth={3}
                          dot={{ r: 4, strokeWidth: 0, fill: "#f59e0b" }}
                          activeDot={{ r: 8, strokeWidth: 0 }}
                        />
                        <Line
                          name="Internal Completed Rides"
                          type="monotone"
                          dataKey="internalCompleted"
                          stroke="#10b981"
                          strokeWidth={3}
                          strokeDasharray="6 3"
                          dot={{ r: 4, strokeWidth: 0, fill: "#10b981" }}
                          activeDot={{ r: 8, strokeWidth: 0 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Chart 2: Revenue vs Days */}
            <Grid item sx={{ width: { xs: "100%", sm: "47%" } }}>
              <Card
                sx={{
                  background: "linear-gradient(145deg, #ffffff 0%, #f5f0ff 100%)",
                  border: "1px solid rgba(139,92,246,0.15)",
                  borderTop: "3px solid #8b5cf6",
                  boxShadow: "0 4px 24px rgba(139,92,246,0.08), 0 1px 4px rgba(0,0,0,0.04)",
                  borderRadius: "16px",
                  transition: "transform 0.25s ease, box-shadow 0.25s ease",
                  "&:hover": {
                    transform: "translateY(-5px)",
                    boxShadow: "0 16px 48px rgba(139,92,246,0.15)",
                  },
                }}
              >
                <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
                  <Box display="flex" alignItems="center" gap={1.5} sx={{ mb: 3 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: "50%", background: "linear-gradient(135deg,#8b5cf6,#a78bfa)", boxShadow: "0 0 8px rgba(139,92,246,0.5)" }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#2e1065", letterSpacing: "-0.01em" }}>
                      Revenue Trend
                    </Typography>
                    <Box sx={{ ml: "auto", px: 1.5, py: 0.3, borderRadius: "20px", background: "rgba(139,92,246,0.1)", border: "1px solid rgba(139,92,246,0.2)" }}>
                      <Typography variant="caption" sx={{ color: "#8b5cf6", fontWeight: 600, fontSize: "10px" }}>in ₹</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ width: "100%", height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart
                        data={chartData}
                        margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                      >
                        <defs>
                          <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.2} />
                            <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#ede9fe" vertical={false} />
                        <XAxis dataKey="dateLabel" tick={{ fill: "#7c3aed", fontSize: 12 }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fill: "#7c3aed", fontSize: 12 }} axisLine={false} tickLine={false} tickFormatter={(v) => `₹${v}`} />
                        <ChartTooltip content={<CustomTooltip />} />
                        <Area
                          name="Daily Revenue"
                          type="monotone"
                          dataKey="revenue"
                          stroke="#8b5cf6"
                          strokeWidth={3}
                          fill="url(#revenueGradient)"
                          dot={{ r: 4, strokeWidth: 0, fill: "#8b5cf6" }}
                          activeDot={{ r: 7, strokeWidth: 0, fill: "#7c3aed" }}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Chart 3: Distance vs Days */}
            <Grid item sx={{ width: { xs: "100%", sm: "47%" } }}>
              <Card
                sx={{
                  background: "linear-gradient(145deg, #ffffff 0%, #ecfeff 100%)",
                  border: "1px solid rgba(6,182,212,0.15)",
                  borderTop: "3px solid #06b6d4",
                  boxShadow: "0 4px 24px rgba(6,182,212,0.08), 0 1px 4px rgba(0,0,0,0.04)",
                  borderRadius: "16px",
                  transition: "transform 0.25s ease, box-shadow 0.25s ease",
                  "&:hover": {
                    transform: "translateY(-5px)",
                    boxShadow: "0 16px 48px rgba(6,182,212,0.15)",
                  },
                }}
              >
                <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
                  <Box display="flex" alignItems="center" gap={1.5} sx={{ mb: 3 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: "50%", background: "linear-gradient(135deg,#06b6d4,#67e8f9)", boxShadow: "0 0 8px rgba(6,182,212,0.5)" }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#164e63", letterSpacing: "-0.01em" }}>
                      Distance Trend
                    </Typography>
                    <Box sx={{ ml: "auto", px: 1.5, py: 0.3, borderRadius: "20px", background: "rgba(6,182,212,0.1)", border: "1px solid rgba(6,182,212,0.2)" }}>
                      <Typography variant="caption" sx={{ color: "#06b6d4", fontWeight: 600, fontSize: "10px" }}>in km</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ width: "100%", height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart
                        data={chartData}
                        margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                      >
                        <defs>
                          <linearGradient id="distanceGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.2} />
                            <stop offset="95%" stopColor="#06b6d4" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#cffafe" vertical={false} />
                        <XAxis dataKey="dateLabel" tick={{ fill: "#0891b2", fontSize: 12 }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fill: "#0891b2", fontSize: 12 }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v} km`} />
                        <ChartTooltip content={<CustomTooltip />} />
                        <Area
                          name="Daily Distance"
                          type="monotone"
                          dataKey="distance"
                          stroke="#06b6d4"
                          strokeWidth={3}
                          fill="url(#distanceGradient)"
                          dot={{ r: 4, strokeWidth: 0, fill: "#06b6d4" }}
                          activeDot={{ r: 7, strokeWidth: 0, fill: "#0891b2" }}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Chart 4: Avg Ride Duration */}
            <Grid item sx={{ width: { xs: "100%", sm: "47%" } }}>
              <Card
                sx={{
                  background: "linear-gradient(145deg, #ffffff 0%, #fff7ed 100%)",
                  border: "1px solid rgba(249,115,22,0.15)",
                  borderTop: "3px solid #f97316",
                  boxShadow: "0 4px 24px rgba(249,115,22,0.08), 0 1px 4px rgba(0,0,0,0.04)",
                  borderRadius: "16px",
                  transition: "transform 0.25s ease, box-shadow 0.25s ease",
                  "&:hover": {
                    transform: "translateY(-5px)",
                    boxShadow: "0 16px 48px rgba(249,115,22,0.15)",
                  },
                }}
              >
                <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
                  <Box display="flex" alignItems="center" gap={1.5} sx={{ mb: 3 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: "50%", background: "linear-gradient(135deg,#f97316,#fb923c)", boxShadow: "0 0 8px rgba(249,115,22,0.5)" }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#431407", letterSpacing: "-0.01em" }}>
                      Avg Ride Duration
                    </Typography>
                    <Box sx={{ ml: "auto", px: 1.5, py: 0.3, borderRadius: "20px", background: "rgba(249,115,22,0.1)", border: "1px solid rgba(249,115,22,0.2)" }}>
                      <Typography variant="caption" sx={{ color: "#f97316", fontWeight: 600, fontSize: "10px" }}>in mins</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ width: "100%", height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart
                        data={chartData}
                        margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                      >
                        <defs>
                          <linearGradient id="durationGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#f97316" stopOpacity={0.2} />
                            <stop offset="95%" stopColor="#f97316" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#ffedd5" vertical={false} />
                        <XAxis dataKey="dateLabel" tick={{ fill: "#c2410c", fontSize: 12 }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fill: "#c2410c", fontSize: 12 }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}m`} />
                        <ChartTooltip content={<CustomTooltip />} />
                        <Area
                          name="Avg Duration"
                          type="monotone"
                          dataKey="avgDuration"
                          stroke="#f97316"
                          strokeWidth={3}
                          fill="url(#durationGradient)"
                          dot={{ r: 4, strokeWidth: 0, fill: "#f97316" }}
                          activeDot={{ r: 7, strokeWidth: 0, fill: "#c2410c" }}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {ridesStats !== null && (
        <Box
          width="90%"
          display="flex"
          flexDirection="column"
          justifyContent="flex-start"
          alignItems="flex-start"
          sx={{ mb: -2, borderTop: "1px dashed #555" }}
        >
          <Typography variant="h6">
            <strong>Completed Rides </strong>
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          width: "90%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "flex-start",
          mb: 2,
        }}
      >
        <ol
          style={{
            width: "100%",
          }}
        >
          {ridesStats?.okCompletedRides?.map((ride) => (
            <li key={ride._id}>
              <Box
                sx={{
                  width: "90%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-start",
                  alignItems: "flex-start",
                  mb: 0.1,
                }}
              >
                <Typography variant="p" component="p" textAlign="left">
                  <strong>{ride.customer.fullName}</strong>
                </Typography>

                <Typography
                  variant="p"
                  component="p"
                  textAlign="left"
                  sx={{ ml: 4 }}
                >
                  <strong>Phone Number:</strong>{" "}
                  {ride.customer.phoneNumber.replace("+91", "+91 ")}
                </Typography>

                <Typography
                  variant="p"
                  component="p"
                  textAlign="left"
                  sx={{ ml: 4 }}
                >
                  <strong>Started:</strong> {formatToIST(ride.createdAt)}
                </Typography>

                <Typography
                  variant="p"
                  component="p"
                  textAlign="left"
                  sx={{ ml: 4 }}
                >
                  <strong>Ended:</strong> {formatToIST(ride.updatedAt)}
                </Typography>

                {ride?.fare?.value ? (
                  <Typography
                    variant="p"
                    component="p"
                    textAlign="left"
                    sx={{ ml: 4 }}
                  >
                    <strong>Fare:</strong> ₹ {ride?.fare?.value?.toFixed(2)}
                  </Typography>
                ) : (
                  <Typography
                    variant="p"
                    component="p"
                    textAlign="left"
                    sx={{ ml: 4 }}
                  >
                    <strong>Fare:</strong> N/A
                  </Typography>
                )}

                {ride?.distance !== null && ride?.distance !== undefined ? (
                  <>
                    <Typography
                      variant="p"
                      component="p"
                      textAlign="left"
                      sx={{ ml: 4 }}
                    >
                      <strong>Distance:</strong>{" "}
                      {(ride?.distance / 1_000).toFixed(2)} km
                    </Typography>
                    <Typography
                      variant="p"
                      component="p"
                      textAlign="left"
                      sx={{ ml: 4 }}
                    >
                      <strong>Distance Computation:</strong>{" "}
                      {formatDistanceComputationMethod(
                        ride?.distanceComputationMethod,
                      )}
                    </Typography>
                  </>
                ) : ride?.fare?.value !== null &&
                  ride?.fare?.value !== undefined ? (
                  <>
                    <Typography
                      variant="p"
                      component="p"
                      textAlign="left"
                      sx={{ ml: 4 }}
                    >
                      <strong>Distance:</strong>{" "}
                      {getDistanceFromFare(ride?.fare?.value)?.toFixed(2)} km
                    </Typography>
                    <Typography
                      variant="p"
                      component="p"
                      textAlign="left"
                      sx={{ ml: 4 }}
                    >
                      <strong>Distance Computation:</strong> Price Reverse
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography
                      variant="p"
                      component="p"
                      textAlign="left"
                      sx={{ ml: 4 }}
                    >
                      <strong>Distance:</strong> N/A
                    </Typography>

                    <Typography
                      variant="p"
                      component="p"
                      textAlign="left"
                      sx={{ ml: 4 }}
                    >
                      <strong>Distance Computation:</strong> Error
                    </Typography>
                  </>
                )}

                <Typography
                  variant="body1"
                  component="p"
                  textAlign="left"
                  sx={{ ml: 4 }}
                >
                  <Link
                    href={getGoogleMapsRouteUrl(ride)}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                  >
                    View Route in Google Maps
                  </Link>
                </Typography>
              </Box>
            </li>
          ))}
        </ol>
      </Box>

      {ridesStats !== null && (
        <Box
          width="90%"
          display="flex"
          flexDirection="column"
          justifyContent="flex-start"
          alignItems="flex-start"
          sx={{ mb: -2, borderTop: "1px dashed #555" }}
        >
          <Typography variant="h6">
            <strong>In Progress Rides </strong>
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          width: "90%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "flex-start",
          mb: 2,
        }}
      >
        <ol
          style={{
            width: "100%",
          }}
        >
          {ridesStats?.okInProgressRides?.map((ride) => (
            <li key={ride._id}>
              <Box
                sx={{
                  width: "90%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-start",
                  alignItems: "flex-start",
                  mb: 0.1,
                }}
              >
                <Typography variant="p" component="p" textAlign="left">
                  <strong>{ride.customer.fullName}</strong>
                </Typography>

                <Typography
                  variant="p"
                  component="p"
                  textAlign="left"
                  sx={{ ml: 4 }}
                >
                  <strong>Phone Number:</strong>{" "}
                  {ride.customer.phoneNumber.replace("+91", "+91 ")}
                </Typography>

                <Typography
                  variant="p"
                  component="p"
                  textAlign="left"
                  sx={{ ml: 4 }}
                >
                  <strong>Started:</strong> {formatToIST(ride.createdAt)}
                </Typography>

                <Typography
                  variant="p"
                  component="p"
                  textAlign="left"
                  sx={{ ml: 4 }}
                >
                  <strong>Ended:</strong> {formatToIST(ride.updatedAt)}
                </Typography>
              </Box>
            </li>
          ))}
        </ol>
      </Box>

      {ridesStats !== null && (
        <Box
          width="90%"
          display="flex"
          flexDirection="column"
          justifyContent="flex-start"
          alignItems="flex-start"
          sx={{ mb: -2, borderTop: "1px dashed #555" }}
        >
          <Typography variant="h6">
            <strong>Auto Cancelled Rides </strong>
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          width: "90%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "flex-start",
          mb: 2,
        }}
      >
        <ol
          style={{
            width: "100%",
          }}
        >
          {ridesStats?.okAutoCancelledRides?.map((ride) => (
            <li key={ride._id}>
              <Box
                sx={{
                  width: "90%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-start",
                  alignItems: "flex-start",
                  mb: 0.1,
                }}
              >
                <Typography variant="p" component="p" textAlign="left">
                  <strong>{ride.customer.fullName}</strong>
                </Typography>

                <Typography
                  variant="p"
                  component="p"
                  textAlign="left"
                  sx={{ ml: 4 }}
                >
                  <strong>Phone Number:</strong>{" "}
                  {ride.customer.phoneNumber.replace("+91", "+91 ")}
                </Typography>

                <Typography
                  variant="p"
                  component="p"
                  textAlign="left"
                  sx={{ ml: 4 }}
                >
                  <strong>Started:</strong> {formatToIST(ride.createdAt)}
                </Typography>

                <Typography
                  variant="p"
                  component="p"
                  textAlign="left"
                  sx={{ ml: 4 }}
                >
                  <strong>Ended:</strong> {formatToIST(ride.updatedAt)}
                </Typography>
              </Box>
            </li>
          ))}
        </ol>
      </Box>

      {ridesStats !== null && (
        <Box
          width="90%"
          display="flex"
          flexDirection="column"
          justifyContent="flex-start"
          alignItems="flex-start"
          sx={{ mb: -2, borderTop: "1px dashed #555" }}
        >
          <Typography variant="h6">
            <strong>Manually Cancelled Rides </strong>
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          width: "90%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "flex-start",
          mb: 2,
        }}
      >
        <ol
          style={{
            width: "100%",
          }}
        >
          {ridesStats?.okManuallyCancelledRides?.map((ride) => (
            <li key={ride._id}>
              <Box
                sx={{
                  width: "90%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-start",
                  alignItems: "flex-start",
                  mb: 0.1,
                }}
              >
                <Typography variant="p" component="p" textAlign="left">
                  <strong>{ride.customer.fullName}</strong>
                </Typography>

                <Typography
                  variant="p"
                  component="p"
                  textAlign="left"
                  sx={{ ml: 4 }}
                >
                  <strong>Phone Number:</strong>{" "}
                  {ride.customer.phoneNumber.replace("+91", "+91 ")}
                </Typography>

                <Typography
                  variant="p"
                  component="p"
                  textAlign="left"
                  sx={{ ml: 4 }}
                >
                  <strong>Started:</strong> {formatToIST(ride.createdAt)}
                </Typography>

                <Typography
                  variant="p"
                  component="p"
                  textAlign="left"
                  sx={{ ml: 4 }}
                >
                  <strong>Ended:</strong> {formatToIST(ride.updatedAt)}
                </Typography>
              </Box>
            </li>
          ))}
        </ol>
      </Box>
    </Box>
  );
}
